import { agencyService } from '../services/agencyService';
import { subscriberService } from '../services/subscriberService';

// Cache for agency and subscriber names to avoid repeated API calls
const agencyNameCache = new Map<number, string>();
const subscriberNameCache = new Map<number, string>();

/**
 * Fetches and caches agency name by ID
 */
export const getAgencyName = async (agencyId: number): Promise<string> => {
  if (agencyNameCache.has(agencyId)) {
    return agencyNameCache.get(agencyId)!;
  }

  try {
    const response = await agencyService.getById(agencyId);
    const agencyName = response.data.agencyName || 'Agence inconnue';
    agencyNameCache.set(agencyId, agencyName);
    return agencyName;
  } catch (error) {
    console.error(`Error fetching agency name for ID ${agencyId}:`, error);
    const fallbackName = `Agence #${agencyId}`;
    agencyNameCache.set(agencyId, fallbackName);
    return fallbackName;
  }
};

/**
 * Fetches and caches subscriber name by ID
 */
export const getSubscriberName = async (subscriberId: number): Promise<string> => {
  if (subscriberNameCache.has(subscriberId)) {
    return subscriberNameCache.get(subscriberId)!;
  }

  try {
    // Get all subscribers and find the one with matching idSub
    const response = await subscriberService.getAll();
    const subscriber = response.data.find((sub: any) => sub.idSub === subscriberId);
    
    if (subscriber) {
      const fullName = `${subscriber.firstName} ${subscriber.lastName}`.trim();
      subscriberNameCache.set(subscriberId, fullName);
      return fullName;
    } else {
      const fallbackName = `Abonné #${subscriberId}`;
      subscriberNameCache.set(subscriberId, fallbackName);
      return fallbackName;
    }
  } catch (error) {
    console.error(`Error fetching subscriber name for ID ${subscriberId}:`, error);
    const fallbackName = `Abonné #${subscriberId}`;
    subscriberNameCache.set(subscriberId, fallbackName);
    return fallbackName;
  }
};

/**
 * Clears the name caches (useful when data might have changed)
 */
export const clearNameCaches = (): void => {
  agencyNameCache.clear();
  subscriberNameCache.clear();
};

/**
 * Preloads agency names for better performance
 */
export const preloadAgencyNames = async (): Promise<void> => {
  try {
    const response = await agencyService.getAll();
    response.data.agencies?.forEach((agency: any) => {
      agencyNameCache.set(agency.id, agency.agencyName || 'Agence inconnue');
    });
  } catch (error) {
    console.error('Error preloading agency names:', error);
  }
};

/**
 * Preloads subscriber names for better performance
 */
export const preloadSubscriberNames = async (): Promise<void> => {
  try {
    const response = await subscriberService.getAll();
    response.data.forEach((subscriber: any) => {
      const fullName = `${subscriber.firstName} ${subscriber.lastName}`.trim();
      subscriberNameCache.set(subscriber.idSub, fullName);
    });
  } catch (error) {
    console.error('Error preloading subscriber names:', error);
  }
};